import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { orgGetInvitation, orgConsentDecision } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ConsentLink() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr("Kein Token.");
      return;
    }
    orgGetInvitation(token)
      .then(setInfo)
      .catch((e) => setErr(e?.response?.data?.detail || "Anfrage ungültig oder abgelaufen."));
  }, [token]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await orgConsentDecision(token, decision);
      await refresh();
      toast.success(decision === "accept" ? "Verknüpfung bestätigt." : "Verknüpfung abgelehnt.");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
      setBusy(false);
    }
  };

  if (loading)
    return (
      <AuthShell title="Verknüpfung">
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AuthShell>
    );

  if (!user)
    return (
      <AuthShell
        title="Anmeldung erforderlich"
        subtitle="Bitte melden Sie sich an, um die Verknüpfung zu bestätigen."
      >
        <Link to="/login" className="block">
          <Button className="w-full" data-testid="consent-login">Zur Anmeldung</Button>
        </Link>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Öffnen Sie anschließend den Link aus der E-Mail erneut.
        </p>
      </AuthShell>
    );

  if (err)
    return (
      <AuthShell title="Verknüpfung">
        <p className="text-sm text-destructive text-center" data-testid="consent-error">{err}</p>
      </AuthShell>
    );

  if (!info)
    return (
      <AuthShell title="Verknüpfung">
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AuthShell>
    );

  return (
    <AuthShell
      title="Verknüpfung bestätigen"
      subtitle={`${info.inviter_name} möchte Ihr Konto als ${info.role_label} verknüpfen.`}
    >
      <div className="space-y-3" data-testid="consent-actions">
        <p className="text-sm text-muted-foreground">
          Nach Ihrer Zustimmung erhält {info.inviter_name} Zugriff auf Ihr Konto. Sie können die
          Verknüpfung später wieder lösen.
        </p>
        <Button className="w-full" disabled={busy} onClick={() => decide("accept")} data-testid="consent-accept">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verknüpfung bestätigen"}
        </Button>
        <Button
          variant="secondary"
          className="w-full border border-border"
          disabled={busy}
          onClick={() => decide("decline")}
          data-testid="consent-decline"
        >
          Ablehnen
        </Button>
      </div>
    </AuthShell>
  );
}
